import { api } from './api.js';

/**
 * Uploading a photo and getting its analysis back.
 *
 * In the cloud the bytes never pass through the API — a phone photo is several
 * megabytes and there is no reason to spend the API's request budget carrying
 * it. The browser asks for a short-lived signed upload URL, PUTs straight to
 * the bucket, then names the uploaded paths in a second call.
 *
 * Locally there is no bucket, so the same call reports `direct: false` and the
 * files go up as ordinary multipart. One code path either way.
 *
 * The analysis itself runs inside that second call and the answer is already
 * finished, so nothing here polls: the host lets a request stay open for the
 * 30-90 seconds a vision call takes.
 */

const CONTENT_TYPES = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
};

/**
 * Mirrors MAX_PHOTOS in server/src/routes/jobs.js. Checked here as well as
 * there so picking twenty screenshots says so immediately, rather than after a
 * round trip -- and so it can never again be discovered by uploading six files
 * and then failing.
 */
const MAX_PHOTOS = 6;

function contentTypeOf(file) {
  if (file.type && file.type.startsWith('image/')) return file.type;
  const extension = file.name?.split('.').pop()?.toLowerCase();
  return CONTENT_TYPES[extension] || 'image/jpeg';
}

/**
 * Upload one or more photos and start the analysis.
 * @returns {Promise<{id:number, status:string}>}
 */
export async function startPhotoJob({ jobType, files, locationId = null, onProgress }) {
  const list = [...files];
  if (!list.length) throw new Error('No photo selected.');
  if (list.length > MAX_PHOTOS) {
    throw new Error(
      `Select at most ${MAX_PHOTOS} photos at once — you chose ${list.length}.`,
    );
  }
  const empty = list.find((file) => file.size === 0);
  if (empty) throw new Error(`"${empty.name || 'That file'}" is empty.`);

  onProgress?.('Preparing the upload…');
  // One type per file: a selection can mix a camera JPEG with a screenshot PNG,
  // and the server names each blob from its own file's type.
  const { uploads, direct } = await api.post('/jobs/upload-url', {
    job_type: jobType,
    content_types: list.map(contentTypeOf),
  });

  if (!direct) {
    // Local development: send the bytes to the API and let it work inline.
    onProgress?.('Analyzing…');
    const form = new FormData();
    form.append('job_type', jobType);
    if (locationId) form.append('location_id', String(locationId));
    for (const file of list) form.append('images', file);
    return api.upload('/jobs', form);
  }

  // Fail before uploading anything rather than part-way through. A short list
  // used to surface as a TypeError on the first missing entry, with the earlier
  // files already in storage and no job row to find them by.
  if (!Array.isArray(uploads) || uploads.length !== list.length) {
    throw new Error('The upload could not be prepared for every photo. Try again.');
  }

  for (const [index, file] of list.entries()) {
    const target = uploads[index];
    onProgress?.(list.length > 1 ? `Uploading ${index + 1} of ${list.length}…` : 'Uploading…');
    // Content type only. A signed upload URL carries its authority in the
    // query string, and any additional header makes this cross-origin PUT
    // require a CORS preflight that the storage host has to have allowlisted —
    // so an extra header here is not merely redundant, it is enough to fail
    // every upload against cloud storage.
    let response;
    try {
      response = await fetch(target.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': contentTypeOf(file) },
        body: file,
      });
    } catch (cause) {
      // A refused preflight or a dropped connection rejects the promise rather
      // than returning a response, so `!response.ok` never sees it.
      throw Object.assign(
        new Error('The upload could not reach photo storage. Check your connection and try again.'),
        { cause },
      );
    }
    if (!response.ok) {
      throw new Error(`Upload failed (${response.status}). The link may have expired — try again.`);
    }
  }

  // Nothing is queued: this call is where the analysis happens, and it returns
  // when the job is finished.
  onProgress?.('Analyzing…');
  return api.post('/jobs', {
    job_type: jobType,
    blob_paths: uploads.map((u) => u.blobPath),
    location_id: locationId || undefined,
  });
}

/**
 * Upload and hand back the finished job.
 *
 * There is nothing to wait for: POST /api/jobs runs the analysis in the request
 * and answers 'succeeded' or throws.
 */
export async function runPhotoJob(options) {
  return startPhotoJob(options);
}

/** Where to send the user once a job of this kind finishes. */
export function jobResultPath(job) {
  switch (job.result_kind ?? job.kind) {
    case 'purchase': return `/receipts/${job.result_id ?? job.id}`;
    case 'pantry_scan': return `/scans/${job.result_id ?? job.id}`;
    case 'recipe': return `/recipes/${job.result_id ?? job.id}`;
    default: return '/';
  }
}
